import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid } from "@material-ui/data-grid";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import TextField from "@material-ui/core/TextField";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import GetAppIcon from "@material-ui/icons/GetApp";
import Alert from "@material-ui/lab/Alert";
import { NavBar, Column, OrganizationSelector } from "../../widgets";
import ControlledSnackbar from "../../widgets/ControlledSnackbar";
import {
  fetchSampleReport,
  downloadSampleCsv,
} from "../../data/sample-report-store";
import SampleSummary from "./SampleSummary.jsx";
import SampleStatistics from "./SampleStatistics.jsx";
import {
  METRICS,
  SAMPLE_STATES,
  STATE_LABELS,
  formatMeasurement,
  formatCount,
  formatTimestamp,
  formatDateRange,
} from "./metrics.js";

const NO_ORGANIZATION = { id: "", name: "" };

const useStyles = makeStyles((theme) => ({
  controls: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
    margin: theme.spacing(2),
  },
  dateField: {
    width: 170,
  },
  organizationField: {
    minWidth: 220,
  },
  title: {
    flexGrow: 1,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  summaryRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    margin: theme.spacing(0, 2, 1, 2),
  },
  rangeLabel: {
    flex: "1 1 auto",
    color: theme.palette.text.secondary,
  },
  rangeDates: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  notice: {
    margin: theme.spacing(0, 2, 2, 2),
  },
  grid: {
    flex: "1 1 auto",
    minHeight: 400,
    margin: theme.spacing(0, 2, 2, 2),
  },
  stateChip: {
    display: "inline-block",
    padding: theme.spacing(0.25, 1),
    borderRadius: 4,
    fontSize: "0.75rem",
    fontWeight: 600,
    border: "1px solid",
  },
  [SAMPLE_STATES.ACTIVE]: {
    color: theme.palette.gray.dark,
    borderColor: theme.palette.gray.main,
    backgroundColor: theme.palette.gray.veryLight,
  },
  [SAMPLE_STATES.DELETED]: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    backgroundColor: "#fdecec",
  },
  [SAMPLE_STATES.HIDDEN_PARENT]: {
    color: "#8a5a00",
    borderColor: theme.palette.warning.main,
    backgroundColor: "#fff6e5",
  },
  [SAMPLE_STATES.ORPHANED]: {
    color: theme.palette.primary.dark,
    borderColor: theme.palette.primary.light,
    backgroundColor: theme.palette.secondary.veryLight,
  },
}));

function formatDate(date) {
  const offsetMinutes = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offsetMinutes * 60000);

  return local.toISOString().split("T")[0];
}

function getLastNDaysRange(days) {
  const now = new Date();
  const start = new Date(now.getTime());
  start.setDate(start.getDate() - days);

  return { start: formatDate(start), end: formatDate(now) };
}

function getLastNMonthsRange(months) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - months, 1);

  return { start: formatDate(start), end: formatDate(now) };
}

function getMonthRange(monthsAgo) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
  const end =
    monthsAgo === 0 ? now : new Date(now.getFullYear(), now.getMonth(), 0);

  return { start: formatDate(start), end: formatDate(end) };
}

/**
 * The callable takes a half-open range, so the day the user picked as the end
 * has to be advanced by one to be included in full.
 *
 * @param {string} endDate yyyy-mm-dd
 * @return {string} ISO 8601
 */
function toExclusiveEnd(endDate) {
  const exclusive = new Date(`${endDate}T00:00:00`);
  exclusive.setDate(exclusive.getDate() + 1);

  return exclusive.toISOString();
}

/**
 * The callable SDK reports an HttpsError code as "functions/failed-precondition"
 * while Firestore reports the bare code, so the prefix is stripped before
 * comparing rather than matching one spelling and silently missing the other.
 *
 * @param {Error} error
 * @return {string}
 */
function errorCode(error) {
  return String(error?.code ?? "").replace(/^functions\//, "");
}

export default function SampleReport() {
  const classes = useStyles();

  const defaultRange = getLastNMonthsRange(12);
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [organization, setOrganization] = useState(NO_ORGANIZATION);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);
  const [limitNotice, setLimitNotice] = useState(null);
  const [snackbarState, setSnackbarState] = useState({});

  const renderStateCell = (params) => (
    <span className={`${classes.stateChip} ${classes[params.value]}`}>
      {STATE_LABELS[params.value] ?? params.value}
    </span>
  );

  const columns = [
    { field: "number", headerName: "Location", width: 110, type: "number" },
    {
      field: "state",
      headerName: "State",
      width: 170,
      renderCell: renderStateCell,
    },
    { field: "organizationName", headerName: "Organization", width: 190 },
    { field: "authorName", headerName: "Inspector", width: 170 },
    {
      field: "createdAt",
      headerName: "Recorded",
      width: 180,
      valueFormatter: (params) => formatTimestamp(params.value),
    },
    ...METRICS.map((metric) => ({
      field: metric.key,
      headerName: metric.label,
      width: 120,
      type: "number",
      valueFormatter: (params) => formatMeasurement(params.value),
    })),
    {
      field: "lat",
      headerName: "Latitude",
      width: 130,
      type: "number",
      valueFormatter: (params) => formatMeasurement(params.value),
    },
    {
      field: "lng",
      headerName: "Longitude",
      width: 130,
      type: "number",
      valueFormatter: (params) => formatMeasurement(params.value),
    },
    { field: "inspectionId", headerName: "Inspection", width: 220 },
  ];

  const handleQuickSelect = (preset) => {
    const ranges = {
      last7: () => getLastNDaysRange(7),
      thisMonth: () => getMonthRange(0),
      lastMonth: () => getMonthRange(1),
      last3: () => getLastNMonthsRange(3),
      last12: () => getLastNMonthsRange(12),
      last36: () => getLastNMonthsRange(36),
    };

    const range = ranges[preset]?.();

    if (!range) return;

    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handleGenerate = async () => {
    setLoading(true);
    setReport(null);
    setLimitNotice(null);

    try {
      const data = await fetchSampleReport(
        `${startDate}T00:00:00`,
        toExclusiveEnd(endDate),
        { organizationId: organization.id || null }
      );

      setReport(data);
    } catch (error) {
      // The callable refuses a range it would not finish reading and says how
      // far over the limit it was, which is actionable where a generic failure
      // is not.
      if (errorCode(error) === "failed-precondition") {
        setLimitNotice(error.message);
      } else {
        setSnackbarState({
          open: true,
          message: "Failed to generate the sample report.",
          severity: "error",
          error: error,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Exports through the streaming endpoint rather than the rows already on
   * screen, so the file covers the whole period instead of the capped set the
   * grid shows. At these volumes that difference is most of the data.
   */
  const handleExport = async () => {
    setExporting(true);

    try {
      const suffix = organization.id ? `_${organization.name}` : "";

      await downloadSampleCsv(
        `${startDate}T00:00:00`,
        toExclusiveEnd(endDate),
        { organizationId: organization.id || null },
        `sample-ledger_${startDate}_${endDate}${suffix}.csv`
      );
    } catch (error) {
      setSnackbarState({
        open: true,
        message: error.message ?? "The export failed.",
        severity: "error",
        error: error,
      });
    } finally {
      setExporting(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;

    setSnackbarState({ ...snackbarState, open: false });
  };

  const rows = report?.rows ?? [];
  const hasResults = report != null && rows.length > 0;
  const isEmpty = report != null && rows.length === 0;

  return (
    <React.Fragment>
      <NavBar>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            Sample Ledger
          </Typography>
        </Toolbar>
      </NavBar>

      <div className={classes.controls}>
        <ButtonGroup size="small" variant="outlined">
          <Button onClick={() => handleQuickSelect("last7")}>Last 7 Days</Button>
          <Button onClick={() => handleQuickSelect("thisMonth")}>
            This Month
          </Button>
          <Button onClick={() => handleQuickSelect("lastMonth")}>
            Last Month
          </Button>
          <Button onClick={() => handleQuickSelect("last3")}>
            Last 3 Months
          </Button>
          <Button onClick={() => handleQuickSelect("last12")}>Last Year</Button>
          <Button onClick={() => handleQuickSelect("last36")}>
            Last 3 Years
          </Button>
        </ButtonGroup>

        <TextField
          className={classes.dateField}
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          variant="outlined"
        />
        <TextField
          className={classes.dateField}
          label="End Date"
          type="date"
          value={endDate}
          onChange={(event) => setEndDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          variant="outlined"
        />

        <OrganizationSelector
          className={classes.organizationField}
          variant="outlined"
          size="small"
          organization={organization}
          onChange={setOrganization}
          includeDisabled
        />

        {organization.id !== "" && (
          <Button size="small" onClick={() => setOrganization(NO_ORGANIZATION)}>
            All Organizations
          </Button>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerate}
          disabled={loading || !startDate || !endDate}
        >
          Generate Report
        </Button>
      </div>

      {limitNotice && (
        <Alert severity="warning" className={classes.notice}>
          {limitNotice}
        </Alert>
      )}

      {hasResults && (
        <React.Fragment>
          <SampleSummary counts={report.counts} />

          <SampleStatistics
            statistics={report.statistics}
            deletedCount={report.counts[SAMPLE_STATES.DELETED]}
          />

          {report.truncated.omitted > 0 && (
            <Alert severity="info" className={classes.notice}>
              The table below shows the{" "}
              {formatCount(report.truncated.returned)} most recent of{" "}
              {formatCount(report.counts.total)} samples. Every total and
              statistic above covers all{" "}
              {formatCount(report.counts.total)}. Download the CSV for the
              complete ledger.
            </Alert>
          )}

          <div className={classes.summaryRow}>
            <Typography variant="body2" className={classes.rangeLabel}>
              {/* Echoed from the response, not from the form. The form can be
                  edited after a report runs, and a count shown against the
                  wrong dates is worse than no count at all. */}
              <span className={classes.rangeDates}>
                {formatDateRange(report.range)}
              </span>
              {" • "}
              {report.scope.organizationName ?? "All organizations"}
              {" • "}
              {formatCount(report.counts.total)} samples across{" "}
              {formatCount(report.counts.distinctInspections)} inspections
            </Typography>
            <Button
              variant="outlined"
              startIcon={
                exporting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <GetAppIcon />
                )
              }
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? "Preparing CSV…" : "Download CSV"}
            </Button>
          </div>
        </React.Fragment>
      )}

      {isEmpty && !loading && (
        <Column spacing={2} horizontalAlignment="centerHorizontally">
          <img src="images/NoDocuments@2x.png" alt="" />
          <Typography>No samples were recorded in this period</Typography>
        </Column>
      )}

      {(loading || hasResults) && (
        <DataGrid
          className={classes.grid}
          rows={rows}
          columns={columns}
          loading={loading}
          density="compact"
          disableSelectionOnClick
        />
      )}

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ControlledSnackbar state={snackbarState} onClose={handleSnackbarClose} />
    </React.Fragment>
  );
}
